/**
 * One IndexedDB database for the whole app.
 *
 * Update logs, the photo upload queue, and locally captured photo bytes all live here so
 * that a single `open` upgrade path governs schema changes and a single connection is
 * shared across tabs' worth of subsystems.
 */
export const DB_NAME = 'platewell';
export const DB_VERSION = 2;

export const UPDATES_STORE = 'updates';
export const UPLOADS_STORE = 'uploads';
export const BLOBS_STORE = 'blobs';

let connection: Promise<IDBDatabase> | null = null;

export function openDatabase(): Promise<IDBDatabase> {
  connection ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(UPDATES_STORE)) db.createObjectStore(UPDATES_STORE);
      if (!db.objectStoreNames.contains(UPLOADS_STORE)) db.createObjectStore(UPLOADS_STORE);
      if (!db.objectStoreNames.contains(BLOBS_STORE)) db.createObjectStore(BLOBS_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB upgrade blocked by another tab'));
  });
  return connection;
}

/** Drops the cached connection so a later `openDatabase` reconnects. Used by tests. */
export function resetDatabase(): void {
  connection = null;
}

export function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

export function toBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
